import * as React from 'react';
import { useEffect, useMemo, useRef, useState } from "react";
import * as FocusTrap from "focus-trap-react";
import {handleSpacePressed} from "utils";

type ExpiryAlertProps = {
  /** Raw JWT string (three dot-separated parts). Must include exp claim (seconds since epoch). */
  token: string | null;

  /** Called when user clicks "I'm still here" */
  onStillHere: () => void;

  /** Called when countdown hits 0 (token expired) */
  onExpired: () => void;

  /** How soon before exp to warn (s). Default: 5 minutes. */
  warnWindowSeconds?: number;

  /** Optional: if you refresh token on onStillHere, pass the NEW token and this will reset. */
  className?: string;
};

export function JwtExpiryAlert({
                                 token,
                                 onStillHere,
                                 onExpired,
                                 warnWindowSeconds = 5 * 60,
                                 className,
                               }: ExpiryAlertProps) {

  const expSeconds = useMemo(() => {
    if (!token) return null;
    //const expSeconds = getJwtExpSeconds(token);
    //console.log('expSeconds ', expSeconds);
    //return expSeconds; // ? expSeconds * 1000 : null;
    return getJwtExpSeconds(token);
  }, [token]);

  const [show, setShow] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState<number>(0);

  const timeoutRef = useRef<number | null>(null);
  const intervalRef = useRef<number | null>(null);
  const expiredCalledRef = useRef(false);

  const clearTimers = () => {
    if (timeoutRef.current !== null) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (intervalRef.current !== null) {
      window.clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  useEffect(() => {
    clearTimers();
    expiredCalledRef.current = false;
    setShow(false);
    setSecondsLeft(0);

    if (!expSeconds) return;

    const secondsUntilExp = expSeconds - nowSeconds();

    // Already expired
    if (secondsUntilExp <= 0) {
      expiredCalledRef.current = true;
      onExpired();
      return;
    }

    // When should we start warning?
    const secondsUntilWarn = secondsUntilExp - warnWindowSeconds;
    const startWarning = () => {
      setShow(true);

      // Initialize countdown immediately
      const update = () => {
        const remainingSec = expSeconds - nowSeconds();
       //const remainingSec = Math.max(0, Math.ceil(remainingMs / 1000));
        setSecondsLeft(remainingSec);

        if (remainingSec <= 0 && !expiredCalledRef.current) {
          expiredCalledRef.current = true;
          clearTimers();
          setShow(false);
          onExpired();
        }
      };

      update();

      intervalRef.current = window.setInterval(update, 1000);
    };

    if (secondsUntilWarn <= 0) {
      // We're already inside the warn window
      startWarning();
    } else {
      // Schedule entry into warn window
      timeoutRef.current = window.setTimeout(startWarning, secondsUntilWarn * 1000);
    }

    return () => {
      clearTimers();
    };
    // IMPORTANT: expMs and warnWindowMs drive this; onExpired should be stable (useCallback).
  }, [expSeconds, warnWindowSeconds, onExpired]);

  const handleStillHere = (e) => {
    if(e) {
      e.stopPropagation();
    }
    // Call the provided handler (likely refreshes token)
    onStillHere();

    // Hide the warning UI; if token is refreshed, effect will re-run with new exp
    setShow(false);
    clearTimers();
  };

  if (!show) {
    return null;
  }

  return (

    <div
      className={className}
      style={{
        position: "fixed",
        inset: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "rgba(0,0,0,0.45)",
        zIndex: 9999,
      }}
      role="dialog"
      aria-modal="true"
      aria-label="Session expiring soon"
    >
      <FocusTrap focusTrapOptions={
        {
          allowOutsideClick: false,
          clickOutsideDeactivates: false,
        }
      }>
      <div
        style={{
          width: 420,
          maxWidth: "calc(100vw - 32px)",
          background: "white",
          borderRadius: 12,
          padding: 20,
          boxShadow: "0 10px 30px rgba(0,0,0,0.3)",
        }}
      >
        <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>
          Your session is about to expire
        </div>

        <div style={{ marginBottom: 16, lineHeight: 1.4 }}>
          You’ll be signed out in{" "}
          <strong>{formatSeconds(secondsLeft)}</strong>.
        </div>

        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button
            type="button"
            onClick={handleStillHere}
            onKeyDown={(e)=>{ handleSpacePressed(e, handleStillHere);}}
            style={{
              padding: "10px 12px",
              borderRadius: 10,
              border: "1px solid #ccc",
              background: "#111",
              color: "white",
              cursor: "pointer",
              fontWeight: 600,
            }}
          >
            I’m still here
          </button>
        </div>
      </div>
      </FocusTrap>
    </div>
  );
}

/** Extract exp claim from a JWT (seconds since epoch). Returns null if missing/invalid. */
function getJwtExpSeconds(token: string): number | null {
  const parts = token.split(".");
  if (parts.length !== 3) return null;
  try {
    const payloadJson = base64UrlDecodeToString(parts[1]);
    const payload = JSON.parse(payloadJson) as { exp?: number };
    return typeof payload.exp === "number" ? payload.exp : null;
  } catch {
    return null;
  }
}

/** Base64url decode without external deps */
function base64UrlDecodeToString(b64url: string): string {
  const pad = "=".repeat((4 - (b64url.length % 4)) % 4);
  const b64 = (b64url + pad).replace(/-/g, "+").replace(/_/g, "/");
  const binary = atob(b64);
  // Convert binary string to UTF-8 string
  const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

function formatSeconds(totalSeconds: number): string {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return m > 0 ? `${m}:${String(s).padStart(2, "0")}` : `${s}s`;
}

function nowSeconds() {
  return Math.floor(Date.now() / 1000);
}